import Box from '@mui/material/Box';
import React, { memo, useCallback, useContext, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { ReactReduxContext } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';

import config from '../../config';
import routeConfiguration from '../../routeConfiguration';
import useAppSelector from '../../util/hooks/useAppSelector';
import usePass from '../../util/hooks/usePass';
import useScreenSize from '../../util/hooks/useScreenSize';
import { obfuscatedCoordinates } from '../../util/maps';
import { useMapsLibLoaded } from '../../util/mapsLib';
import { isCordova } from '../../util/mobile/deviceDetection';
import { createResourceLocatorString } from '../../util/routes';
import { LISTING_STATE_DRAFT, LISTING_STATE_PENDING_APPROVAL } from '../../util/types';
import {
  createSlug,
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
} from '../../util/urlHelpers';
import { hasParentWithClassName } from './SearchMap.helpers.js';
import SearchMapWithMapbox from './SearchMapWithMapbox';
// Side-effect import: this module reads the SearchPage / BookmarkPage redux slice, which is injected
// lazily by the duck. Importing it keeps the reducer in whatever chunk this module lands in.
import '../../containers/SearchPage/SearchPage.duck';
import '../../containers/BookmarkPage/BookmarkPage.duck';
import {
  fitMapToBounds,
  getMapBounds,
  getMapCenter,
  INFO_CARD_HANDLE,
  LABEL_HANDLE,
} from './SearchMapWithMapbox.util';

// .root from SearchMap.module.css — applied only when no rootClassName override is passed.
const rootSx = (theme) => ({
  width: '100%',
  height: '100%',
  backgroundColor: theme.tokens.matterColorNegative,
});

const withCoordinatesObfuscated = (listings) => {
  return listings.map((listing) => {
    const { id, attributes, ...rest } = listing;
    const origGeolocation = attributes.geolocation;
    const cacheKey = id ? `${id.uuid}_${origGeolocation.lat}_${origGeolocation.lng}` : null;
    const geolocation = obfuscatedCoordinates(origGeolocation, cacheKey);
    return {
      id,
      ...rest,
      attributes: {
        ...attributes,
        geolocation,
      },
    };
  });
};

const SearchMapContentComponent = ({
  activationToken = 0,
  bounds = null,
  center = null,
  className = null,
  id = 'searchMap',
  isActive = true,
  listings: originalListings = [],
  mapsConfig = config.maps,
  onCloseAsModal = null,
  onMapMoveEnd = () => {},
  rootClassName = null,
  urlCategory = '',
  zoom = 11,
}) => {
  const { store } = useContext(ReactReduxContext);
  const intl = useIntl();
  const navigate = useNavigate();
  const location = useLocation();
  const [annualPassIsValid] = usePass();
  const mapRef = useRef(null);
  const [infoCardOpen, setInfoCardOpen] = useState(null);
  const tab = useAppSelector((state) => state.SearchPage.tab);
  const searchInProgress = useAppSelector((state) => state.SearchPage.searchInProgress);

  // All callbacks below are memoized — they flow through as props to
  // SearchMapWithMapbox, where any reference churn invalidates the
  // `onMoveend` / `initializeMap` useCallback chain and re-fires the
  // marker-sync effect on every parent render. Using useCallback keeps
  // the references stable across renders unless the captured deps
  // actually change.
  const createURLToListing = useCallback(
    (listing) => {
      const routes = routeConfiguration();
      const id = listing.id.uuid;
      const { title, state, publicData } = listing.attributes;
      const { locationPlace = '' } = publicData;
      const slug =
        intl.locale === config.locale
          ? createSlug(`${locationPlace}-${title}`)
          : createSlug(listing.attributes.title);

      const isDraft = state === LISTING_STATE_DRAFT;
      const isPendingApproval = state === LISTING_STATE_PENDING_APPROVAL;
      const variant = isDraft
        ? LISTING_PAGE_DRAFT_VARIANT
        : isPendingApproval
          ? LISTING_PAGE_PENDING_APPROVAL_VARIANT
          : null;

      if (variant) {
        const category = urlCategory || intl.formatMessage({ id: 'ListingUrlCategory.default' });
        return createResourceLocatorString(
          'ListingPageVariant',
          routes,
          { id, slug, variant, category },
          {},
          intl.locale
        );
      }

      const pathParams = { id, slug, ...(urlCategory ? { category: urlCategory } : {}) };
      return createResourceLocatorString('ListingPage', routes, pathParams, {}, intl.locale);
    },
    [intl, urlCategory]
  );

  const onListingInfoCardClicked = useCallback(
    (listing) => {
      if (onCloseAsModal) {
        onCloseAsModal();
      }
      const url = createURLToListing(listing);
      if (isCordova()) {
        navigate(url);
      } else {
        const win = window.open(url, '_blank');
        if (win != null) win.focus();
      }
    },
    [onCloseAsModal, createURLToListing, navigate]
  );

  const onListingClicked = useCallback((listings) => {
    setInfoCardOpen(listings);
  }, []);

  // `infoCardOpen` is read through a ref so the handler stays stable;
  // otherwise it churns on every card open/close.
  const infoCardOpenRef = useRef(infoCardOpen);
  infoCardOpenRef.current = infoCardOpen;
  const onMapClicked = useCallback((e) => {
    const labelClicked = hasParentWithClassName(e.nativeEvent.target, LABEL_HANDLE);
    const infoCardClicked = hasParentWithClassName(e.nativeEvent.target, INFO_CARD_HANDLE);
    const markerClicked =
      e.nativeEvent.target.nodeName === 'IMG' && !e.nativeEvent.target.className.includes('root');
    if (infoCardOpenRef.current != null && !labelClicked && !infoCardClicked && !markerClicked) {
      setInfoCardOpen(null);
    }
  }, []);

  // `bounds` + `activationToken` are read through refs so first-load
  // auto-fit still works without pinning this handler to parent renders.
  const boundsRef = useRef(bounds);
  boundsRef.current = bounds;
  const activationTokenRef = useRef(activationToken);
  activationTokenRef.current = activationToken;
  const onMapLoadHandler = useCallback((map) => {
    mapRef.current = map;
    if (mapRef.current && activationTokenRef.current <= 1) {
      fitMapToBounds(mapRef.current, boundsRef.current, { padding: 0, isAutocompleteSearch: true });
    }
  }, []);

  // Reproduce classNames(rootClassName || css.root, className): the component's own
  // base style applies ONLY when no rootClassName override is passed; className is
  // always appended.
  const classes = [rootClassName, className].filter(Boolean).join(' ') || undefined;
  const rootSxOrNull = rootClassName ? null : rootSx;

  const listingsWithLocation = originalListings.filter((l) => !!l.attributes.geolocation);
  const listingsToUse = mapsConfig.fuzzy.enabled
    ? withCoordinatesObfuscated(listingsWithLocation)
    : listingsWithLocation;

  const { isMobile, isTablet } = useScreenSize();
  const showLoadingIndicator = isMobile || isTablet || isCordova();

  const activeListingId = useAppSelector(
    (state) => state.SearchPage.activeListingId || state.BookmarkPage.activeListingId
  );

  const mapsLibLoaded = useMapsLibLoaded();

  return mapsLibLoaded ? (
    <SearchMapWithMapbox
      activationToken={activationToken}
      activeListingId={activeListingId}
      annualPassIsValid={annualPassIsValid}
      bounds={bounds}
      center={center}
      className={classes}
      createURLToListing={createURLToListing}
      id={id}
      infoCardOpen={infoCardOpen}
      isActive={isActive}
      listings={listingsToUse}
      location={location}
      mapComponentRefreshToken={activationToken}
      onClick={onMapClicked}
      onListingClicked={onListingClicked}
      onListingInfoCardClicked={onListingInfoCardClicked}
      onMapLoad={onMapLoadHandler}
      onMapMoveEnd={onMapMoveEnd}
      searchInProgress={searchInProgress}
      showLoadingIndicator={showLoadingIndicator}
      store={store}
      sx={rootSxOrNull}
      tab={tab}
      urlCategory={urlCategory}
      zoom={zoom}
    />
  ) : (
    <Box className={classes} sx={rootSxOrNull} />
  );
};

const SearchMapContent = memo(SearchMapContentComponent);

SearchMapContent.getMapBounds = getMapBounds;
SearchMapContent.getMapCenter = getMapCenter;

export default SearchMapContent;
